
<div class="space-y-6">
  <!-- Alerts Banner -->
  @if (showExpiredBanner()) {
      <!-- RED BANNER: Expired or Past Due -->
      <div class="bg-red-600 text-white p-4 rounded-xl shadow-lg flex flex-col md:flex-row items-center justify-between gap-4 animate-pulse">
          <div class="flex items-center gap-4">
              <i class="fa-solid fa-circle-exclamation text-3xl"></i>
              <div>
                  <p class="font-bold text-lg">
                      @if(subscription()?.status === 'trialing') { หมดเวลาทดลองใช้แล้ว } 
                      @else { ยอดค้างชำระ / หมดอายุ (Past Due) }
                  </p>
                  <p class="text-red-100 text-sm">กรุณาชำระค่าบริการเพื่อใช้งานต่อเนื่อง ข้อมูลของคุณยังอยู่ครบถ้วน</p>
              </div>
          </div>
          <a routerLink="/admin/billing" class="bg-white text-red-600 px-6 py-2 rounded-lg font-bold hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
              ชำระเงิน / ต่ออายุ
          </a>
      </div>
  } @else if (showTrialBanner()) {
      <!-- BLUE BANNER: Active Trial -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 rounded-xl shadow-lg flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in">
          <div class="flex items-center gap-4">
              <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">
                  🎁
              </div>
              <div>
                  <p class="font-bold text-lg">ทดลองใช้งานฟรี (Free Trial)</p>
                  <p class="text-blue-100 text-sm">คุณสามารถใช้งานฟีเจอร์ทั้งหมดได้อีก <span class="font-black text-white underline text-lg">{{ trialDaysLeft() }} วัน</span></p>
              </div>
          </div>
          <a routerLink="/admin/billing" class="bg-white text-blue-700 px-6 py-2 rounded-lg font-bold hover:bg-blue-50 transition-colors whitespace-nowrap shadow-sm cursor-pointer">
              อัปเกรดเพื่อใช้งานถาวร
          </a>
      </div>
  }

  <!-- Header -->
  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    <div>
        <h1 class="text-2xl font-bold text-gray-800">{{ 'backOffice.dashboard.title' | translate }}</h1>
        <p class="text-gray-500 text-sm">{{ 'backOffice.dashboard.subtitle' | translate }}</p>
    </div>
    <div class="text-sm bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full border border-emerald-200">
        <i class="fa-solid fa-circle-check mr-1"></i> {{ 'backOffice.dashboard.systemStatus' | translate }}
    </div>
  </div>

  @switch (loadingState()) {
    @case ('loading') {
      <div class="flex flex-col items-center justify-center py-20 text-gray-500">
        <i class="fa-solid fa-spinner animate-spin text-4xl mb-4"></i>
        <p class="text-lg font-semibold">{{ 'backOffice.dashboard.loading' | translate }}</p>
      </div>
    }
    @case ('error') {
      <div class="bg-red-100 border-l-4 border-red-500 text-red-800 p-6 rounded-md my-4" role="alert">
        <p class="font-bold text-lg mb-2">Error</p>
        <p>Could not load dashboard data.</p>
      </div>
    }
    @case ('loaded') {
        <!-- KPI Cards -->
        <div id="kpi-section" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          @for(kpi of kpis(); track kpi.title) {
            <app-kpi-card [kpi]="kpi"></app-kpi-card>
          }
        </div>
      
        <!-- Real Plan Usage -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div id="plan-usage-card" class="bg-white p-6 rounded-md shadow-sm border border-gray-200">
                <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <i class="fa-solid fa-chart-simple text-blue-600"></i> {{ 'backOffice.dashboard.planUsage.title' | translate }}
                </h2>
                <div class="flex items-end justify-between mb-2">
                    <span class="text-gray-600">{{ 'backOffice.dashboard.planUsage.caseLimit' | translate }}</span>
                    <span class="font-bold text-gray-900">
                        {{ usage()?.medical_cases_used }} / {{ subscription()?.plan?.limits?.['medical_cases'] || '500' }} Cases
                    </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div class="bg-blue-600 h-4 rounded-full transition-all duration-1000" [style.width.%]="usageProgress()"></div>
                </div>
                <p class="text-xs text-gray-500 mt-3 flex items-center gap-1">
                    {{ 'backOffice.dashboard.planUsage.currentPlan' | translate }}: <strong class="text-slate-800">{{ currentPlanName() }}</strong>
                </p>
                <div class="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center justify-between">
                    <span class="text-xs text-blue-700 font-bold">รอบการใช้งานปัจจุบันจะรีเซ็ตในวันที่:</span>
                    <span class="text-sm font-black text-blue-800">
                        {{ usage()?.next_reset_date | date:'d MMMM yyyy' }}
                    </span>
                </div>
            </div>

            <div id="quick-actions-card" class="bg-white p-6 rounded-md shadow-sm border border-gray-200">
                <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <i class="fa-solid fa-bolt text-yellow-500"></i> {{ 'backOffice.dashboard.quickActions.title' | translate }}
                </h2>
                <div class="flex gap-3">
                    <a routerLink="/admin/customers" class="flex-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 p-4 rounded-lg text-center transition-colors border border-emerald-200 cursor-pointer">
                        <i class="fa-solid fa-user-plus text-2xl mb-2"></i>
                        <div class="font-bold">{{ 'backOffice.dashboard.quickActions.registerPatient' | translate }}</div>
                    </a>
                    <a routerLink="/admin/tele-medicine" class="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 p-4 rounded-lg text-center transition-colors border border-blue-200 cursor-pointer">
                        <i class="fa-solid fa-user-doctor text-2xl mb-2"></i>
                        <div class="font-bold">{{ 'backOffice.dashboard.quickActions.doctorStation' | translate }}</div>
                    </a>
                </div>
            </div>
        </div>
    }
  }
</div>
